import React from "react";

export default function UpdatePriceModal({
    open,
    onClose,
    results, // Array of { variantKey, variantSkuId, finalPrice, wholesaleFinalPrice }
    dimensions, // { weight, length, width, height }
    isProcessing,
    progress, // Number 0-100
}) {
    if (!open) return null;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center"
        >
            <div
                className="bg-white w-full max-w-2xl max-h-[75vh] overflow-y-auto p-4 shadow-xl rounded-lg relative z-[10000] update-price-modal"
                style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#cbd5e0 #f7fafc'
                }}
            >
                <style>{`
                    .update-price-modal::-webkit-scrollbar {
                        width: 8px;
                    }
                    .update-price-modal::-webkit-scrollbar-track {
                        background: #f7fafc;
                        border-radius: 4px;
                    }
                    .update-price-modal::-webkit-scrollbar-thumb {
                        background: #cbd5e0;
                        border-radius: 4px;
                    }
                    .update-price-modal::-webkit-scrollbar-thumb:hover {
                        background: #a0aec0;
                    }
                `}</style>
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold">
                        {isProcessing ? "Processing..." : "Apply to All Variants - Results"}
                    </h3>
                    {!isProcessing && (
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-gray-600 hover:text-gray-900 text-xl"
                        >
                            ✕
                        </button>
                    )}
                </div>

                {/* Processing State */}
                {isProcessing && (
                    <div className="py-6">
                        <div className="text-center mb-3">
                            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-3"></div>
                            <p className="text-gray-700 font-medium text-sm">Calculating prices for all variants...</p>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                            <div
                                className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                                style={{ width: `${progress || 0}%` }}
                            ></div>
                        </div>
                        <div className="text-center text-xs text-gray-600">
                            {progress || 0}% Complete
                        </div>
                    </div>
                )}

                {/* Weight and Dimensions Info */}
                {!isProcessing && dimensions && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Weight & Dimensions:</h4>
                        <div className="grid grid-cols-4 gap-2 text-sm">
                            {dimensions.weight !== null && dimensions.weight !== undefined && (
                                <div className="flex items-center">
                                    <span className="text-gray-600 font-medium">Weight:</span>
                                    <span className="ml-2 text-gray-900">{dimensions.weight} kg</span>
                                </div>
                            )}
                            {dimensions.length !== null && dimensions.length !== undefined && (
                                <div className="flex items-center">
                                    <span className="text-gray-600 font-medium">Length:</span>
                                    <span className="ml-2 text-gray-900">{dimensions.length} cm</span>
                                </div>
                            )}
                            {dimensions.width !== null && dimensions.width !== undefined && (
                                <div className="flex items-center">
                                    <span className="text-gray-600 font-medium">Width:</span>
                                    <span className="ml-2 text-gray-900">{dimensions.width} cm</span>
                                </div>
                            )}
                            {dimensions.height !== null && dimensions.height !== undefined && (
                                <div className="flex items-center">
                                    <span className="text-gray-600 font-medium">Height:</span>
                                    <span className="ml-2 text-gray-900">{dimensions.height} cm</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Results Table */}
                {!isProcessing && results && results.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-300 text-sm">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-xs">
                                        Variant SKU ID
                                    </th>
                                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-xs">
                                        Final Price (FBP)
                                    </th>
                                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-xs">
                                        WS Final Price
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((result, index) => (
                                    <tr key={result.variantKey || index} className="hover:bg-gray-50">
                                        <td className="border border-gray-300 px-3 py-2 text-xs">
                                            {result.variantSkuId || result.variantKey || "N/A"}
                                        </td>
                                        <td className="border border-gray-300 px-3 py-2 text-xs">
                                            {result.finalPrice !== null && result.finalPrice !== undefined && result.finalPrice > 0
                                                ? `₹${result.finalPrice.toFixed(0)}`
                                                : "---"}
                                        </td>
                                        <td className="border border-gray-300 px-3 py-2 text-xs">
                                            {result.wholesaleFinalPrice !== null && result.wholesaleFinalPrice !== undefined && result.wholesaleFinalPrice > 0
                                                ? `₹${result.wholesaleFinalPrice.toFixed(0)}`
                                                : "---"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* No Results */}
                {!isProcessing && (!results || results.length === 0) && (
                    <div className="text-center py-6 text-gray-500 text-sm">
                        No results available
                    </div>
                )}

                {/* Close Button */}
                {!isProcessing && (
                    <div className="mt-4 flex justify-end">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

